import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Wrench, Truck, Sparkles } from "lucide-react";
import { apiClient } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const HERO_IMG = "/hero-cover.png";
const STORY_IMG =
  "https://images.unsplash.com/photo-1558981806-ec527fa84c39?crop=entropy&cs=srgb&fm=jpg&q=85";

const MARQUEE = [
  "RIDE LOUD",
  "RIDE CUSTOM",
  "WOMAN OWNED",
  "ROAD TESTED",
  "HAND FINISHED",
  "MADE IN THE USA",
];

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    apiClient
      .get("/products", { params: { featured: true } })
      .then((r) => setFeatured(r.data.slice(0, 6)))
      .catch(() => setFeatured([]));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative h-[88vh] overflow-hidden bgc-grain">
        <img
          src={HERO_IMG}
          alt="Woman rider at sunset"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/50 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 h-full flex items-end pb-24">
          <div className="max-w-2xl">
            <div className="bgc-eyebrow text-[#c8102e] mb-6" data-testid="hero-eyebrow">
              EST. 2019 · WOMAN OWNED · ROAD TESTED
            </div>
            <h1 className="font-display text-[clamp(3rem,9vw,8rem)] leading-[0.9] tracking-tight uppercase">
              Ride Loud.
              <br />
              <span className="text-[#c8102e]">Ride Custom.</span>
            </h1>
            <p className="mt-8 text-lg text-[#d1d5db] leading-relaxed max-w-xl">
              Hand-finished mirrors, hardware, and apparel for the women who refuse to ride stock. Built in our garage. Tested on the road. Shipped to yours.
            </p>
            <div className="flex flex-wrap gap-4 mt-10">
              <Link to="/shop" className="btn-primary" data-testid="hero-shop-button">
                SHOP THE COLLECTION <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/about" className="btn-ghost" data-testid="hero-story-button">
                OUR STORY
              </Link>
            </div>
          </div>
        </div>

        {/* Brand patch top-right of hero */}
        <img
          src="/logo.png"
          alt="Bad Girl Customs"
          data-testid="hero-logo-patch"
          className="hidden md:block absolute top-28 right-10 lg:right-16 w-36 lg:w-48 z-10 mix-blend-screen opacity-95 drop-shadow-[0_8px_30px_rgba(200,16,46,0.35)]"
        />
      </section>

      {/* Marquee strip */}
      <section className="border-y border-white/10 bg-[#0a0a0a] py-5 overflow-hidden">
        <div className="flex marquee-track whitespace-nowrap gap-12">
          {[...MARQUEE, ...MARQUEE, ...MARQUEE].map((m, i) => (
            <span
              key={i}
              className="font-display text-2xl tracking-[0.3em] text-white/80 flex items-center gap-12"
            >
              {m}
              <span className="text-[#c8102e]">✦</span>
            </span>
          ))}
        </div>
      </section>

      {/* Categories tetris grid */}
      <section className="py-24 md:py-32" data-testid="categories-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-6">
            <div>
              <div className="bgc-eyebrow text-[#c8102e]">THE GOODS</div>
              <h2 className="font-display text-5xl md:text-6xl tracking-tight uppercase mt-3">
                Built for <span className="text-[#c8102e]">Bad Girls</span>.
              </h2>
            </div>
            <Link to="/shop" className="btn-ghost" data-testid="categories-shop-all">
              SHOP ALL <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            <CategoryTile
              to="/shop/mirrors"
              label="Mirrors"
              tagline="The flagship."
              image="https://images.unsplash.com/photo-1671834214096-6aa88bd6470d?crop=entropy&cs=srgb&fm=jpg&q=85"
              className="md:col-span-7 aspect-[16/10]"
              testid="category-mirrors"
            />
            <CategoryTile
              to="/shop/floorboards"
              label="Floorboards"
              tagline="Step up your ride."
              image="https://images.unsplash.com/photo-1759776421774-78939d87f201?crop=entropy&cs=srgb&fm=jpg&q=85"
              className="md:col-span-5 aspect-[16/10]"
              testid="category-floorboards"
            />
            <CategoryTile
              to="/shop/apparel"
              label="Apparel"
              tagline="Tees, hoodies, leather."
              image="https://images.unsplash.com/photo-1755541608110-3440a0f168ed?crop=entropy&cs=srgb&fm=jpg&q=85"
              className="md:col-span-12 aspect-[21/8]"
              testid="category-apparel"
            />
          </div>
        </div>
      </section>

      {/* Featured products */}
      <section className="py-24 md:py-32 bg-[#080808]" data-testid="featured-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-12">
            <div>
              <div className="bgc-eyebrow text-[#c8102e]">FEATURED</div>
              <h2 className="font-display text-5xl tracking-tight uppercase mt-3">
                {`This Month's Pick.`}
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((p, i) => (
              <ProductCard product={p} key={p.id} index={i} />
            ))}
            {featured.length === 0 && (
              <p className="text-[#9ca3af] col-span-full">Loading products…</p>
            )}
          </div>
        </div>
      </section>

      {/* Wear the Patch — Apparel strip */}
      <section className="relative py-24 md:py-32 bg-[#080808] overflow-hidden bgc-grain" data-testid="apparel-strip">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-center relative z-10">
          <div className="md:col-span-5">
            <div className="bgc-eyebrow text-[#c8102e]">WEAR THE PATCH</div>
            <h2 className="font-display text-5xl md:text-6xl tracking-tight uppercase mt-3 leading-none">
              Off the bike. <br />
              <span className="text-[#c8102e]">Still loud.</span>
            </h2>
            <p className="text-[#d1d5db] mt-6 text-lg leading-relaxed max-w-md">
              Heavyweight tees and brushed-fleece hoodies, screen-printed in-house with our winged-skull patch. Built to be lived in.
            </p>
            <Link to="/shop/apparel" className="btn-primary mt-8" data-testid="apparel-shop-cta">
              SHOP APPAREL <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="md:col-span-7 grid grid-cols-2 gap-4">
            <img src="/apparel/tee-front.png" alt="Black tee" className="w-full aspect-square object-cover border border-white/10 hover:border-[#c8102e] transition-colors" />
            <img src="/apparel/hoodie-front.png" alt="Black hoodie" className="w-full aspect-square object-cover border border-white/10 hover:border-[#c8102e] transition-colors translate-y-6" />
            <img src="/apparel/tee-pink.png" alt="Pink tee" className="w-full aspect-square object-cover border border-white/10 hover:border-[#c8102e] transition-colors -translate-y-6" />
            <img src="/apparel/hoodie-grey.png" alt="Grey hoodie" className="w-full aspect-square object-cover border border-white/10 hover:border-[#c8102e] transition-colors" />
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 md:py-32" data-testid="values-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-3 gap-10">
          <ValueCard
            icon={<Wrench className="w-7 h-7" />}
            title="Built by Hand"
            text="Every mirror is finished one at a time in our garage in the Arizona desert."
          />
          <ValueCard
            icon={<Sparkles className="w-7 h-7" />}
            title="Lifetime Hardware"
            text="Marine-grade stainless and aerospace-spec aluminum. We back every fastener."
          />
          <ValueCard
            icon={<Truck className="w-7 h-7" />}
            title="Free U.S. Shipping"
            text="On orders over $150. Discreetly packaged and out the door in 48 hours."
          />
        </div>
      </section>

      {/* Story strip */}
      <section className="relative py-24 md:py-32 bg-[#0a0a0a] border-y border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <img
              src={STORY_IMG}
              alt="Founder on the road"
              className="w-full aspect-[4/5] object-cover border border-white/10"
            />
          </div>
          <div className="md:col-span-7">
            <div className="bgc-eyebrow text-[#c8102e]">OUR STORY</div>
            <h2 className="font-display text-5xl md:text-6xl tracking-tight uppercase mt-3 leading-none">
              We Started <br />
              <span className="text-[#c8102e]">Because Nobody Else Would.</span>
            </h2>
            <p className="text-[#d1d5db] mt-6 text-lg leading-relaxed max-w-xl">
              {`Bad Girl Customs began with a beat-up Sportster, a lathe, and a refusal to settle for pink-tinted "for her" parts. Today we ship custom mirrors and floorboards to riders in all 50 states — still woman-owned, still ride-tested.`}
            </p>
            <Link to="/about" className="btn-primary mt-8" data-testid="story-cta">
              READ THE STORY <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function CategoryTile({ to, label, tagline, image, className = "", testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`relative overflow-hidden group border border-white/10 ${className}`}
    >
      <img
        src={image}
        alt={label}
        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-transparent" />
      <div className="absolute bottom-0 left-0 p-8">
        <p className="bgc-eyebrow text-white/70">{tagline}</p>
        <h3 className="font-display text-4xl md:text-5xl tracking-tight uppercase mt-2 group-hover:text-[#c8102e] transition-colors">
          {label}
        </h3>
      </div>
    </Link>
  );
}

function ValueCard({ icon, title, text }) {
  return (
    <div className="border border-white/10 p-8 hover:border-[#c8102e] transition-colors">
      <div className="text-[#c8102e] mb-5">{icon}</div>
      <h3 className="font-display text-2xl tracking-wider uppercase">{title}</h3>
      <p className="text-[#9ca3af] mt-3 leading-relaxed">{text}</p>
    </div>
  );
}
